<template>
  <section class="how-it-works">
    <div class="mx-auto max-w-screen-xl py-16 md:px-6 md:py-24 lg:py-32">
      <div
        class="mx-auto flex max-w-2xl flex-col items-center gap-4 px-4 text-center md:px-0"
      >
        <span class="how-it-works__eyebrow">{{ $t('eyebrow') }}</span>
        <h2 class="how-it-works__title">{{ $t('title') }}</h2>
        <p class="how-it-works__subtitle">{{ $t('subtitle') }}</p>
      </div>

      <div ref="viewport" class="how-it-works__viewport mt-8 md:mt-16">
        <div class="how-it-works__track">
          <template v-for="(step, index) in steps">
            <!-- v-track-visibility reads metadata once in `inserted`, before
                 `mounted` sets the breakpoint; the key forces a re-read. -->
            <div
              :id="`pd10913-how-it-works-step-${index + 1}`"
              :key="`${step.key}-${isCarousel}`"
              v-track-visibility.quiet.once="visibilityBinding(index)"
              class="how-it-works__card"
            >
              <span class="how-it-works__badge">{{ index + 1 }}</span>
              <div class="how-it-works__icon">
                <InlineSvg
                  :src="step.icon"
                  alt=""
                  class="h-full w-full p-2.5"
                />
              </div>
              <h3 class="how-it-works__card-title">{{ $t(step.titleKey) }}</h3>
              <p class="how-it-works__card-body">{{ $t(step.bodyKey) }}</p>
              <span class="how-it-works__pill">{{ $t(step.pillKey) }}</span>
            </div>

            <InlineSvg
              v-if="!isCarousel && index < steps.length - 1"
              :key="`${step.key}-arrow`"
              src="images/pd-10913/arrow.svg"
              alt=""
              class="how-it-works__arrow hidden h-8 w-8 shrink-0 md:block md:self-center"
            />
          </template>
        </div>
      </div>

      <div v-if="isCarousel" class="how-it-works__dots">
        <button
          v-for="(step, index) in steps"
          :key="`${step.key}-dot`"
          type="button"
          class="how-it-works__dot"
          :class="{ 'how-it-works__dot--active': index === activeIndex }"
          :aria-current="index === activeIndex ? 'true' : 'false'"
          :aria-label="$t('dotLabel', { step: index + 1 })"
          @click="scrollTo(index)"
        />
      </div>

      <div
        class="mx-auto mt-10 flex max-w-[446px] flex-col items-center gap-4 px-4 md:mt-16 md:px-0"
      >
        <button
          v-track-click
          type="button"
          class="how-it-works__cta"
          @click="goToForm"
        >
          {{ $t('cta') }}
        </button>
        <p class="how-it-works__trust">{{ $t('trust') }}</p>
      </div>
    </div>
  </section>
</template>

<script>
import InlineSvg from 'vue-inline-svg'
import { markFunnelEntered } from '~/middleware/VariantGuard.PD-10913'

/**
 * Above this width the cards are a plain flex row, as control's desktop is.
 */
const DESKTOP_QUERY = '(min-width: 768px)'

export default {
  name: 'HowItWorksSection',

  components: {
    InlineSvg,
  },

  /**
   * @returns {object}
   */
  data() {
    return {
      activeIndex: 0,
      isCarousel: false,
      steps: [
        {
          key: 'step1',
          icon: 'images/pd-10913/step-1.svg',
          titleKey: 'step1Title',
          bodyKey: 'step1Body',
          pillKey: 'step1Pill',
        },
        {
          key: 'step2',
          icon: 'images/pd-10913/step-2.svg',
          titleKey: 'step2Title',
          bodyKey: 'step2Body',
          pillKey: 'step2Pill',
        },
        {
          key: 'step3',
          icon: 'images/pd-10913/step-3.svg',
          titleKey: 'step3Title',
          bodyKey: 'step3Body',
          pillKey: 'step3Pill',
        },
      ],
    }
  },

  /**
   * Embla is client-only and lives outside the reactive data on purpose:
   * Vue's observer walking its internals breaks the engine.
   */
  mounted() {
    this.embla = undefined
    this.carouselGeneration = 0
    this.mediaQuery = window.matchMedia(DESKTOP_QUERY)

    this.onBreakpointChange = () => this.syncCarousel()

    // Safari below 14 has no addEventListener on MediaQueryList.
    if (this.mediaQuery.addEventListener) {
      this.mediaQuery.addEventListener('change', this.onBreakpointChange)
    } else {
      this.mediaQuery.addListener(this.onBreakpointChange)
    }

    this.syncCarousel()
  },

  /**
   *
   */
  beforeDestroy() {
    if (this.mediaQuery.removeEventListener) {
      this.mediaQuery.removeEventListener('change', this.onBreakpointChange)
    } else {
      this.mediaQuery.removeListener(this.onBreakpointChange)
    }

    this.carouselGeneration += 1
    this.destroyCarousel()
  },

  methods: {
    /**
     * Metadata rides along on the visibility event so mobile card views, which
     * need a swipe, can be segmented from desktop ones, which do not.
     *
     * @param {number} index
     * @returns {object}
     */
    visibilityBinding(index) {
      return {
        metadata: {
          step: index + 1,
          breakpoint: this.isCarousel ? 'mobile' : 'desktop',
        },
        options: { threshold: 0.5 },
      }
    },

    /**
     * @returns {Promise<void>}
     */
    async syncCarousel() {
      const shouldCarousel = !this.mediaQuery.matches

      if (shouldCarousel === this.isCarousel) {
        return
      }

      this.isCarousel = shouldCarousel

      // A later call or an unmount bumps this, so an await resuming too late
      // gives up. destroyCarousel can't undo an import still in flight.
      const generation = (this.carouselGeneration += 1)

      // Let the arrows leave the DOM before Embla measures its slides.
      await this.$nextTick()

      if (generation !== this.carouselGeneration) {
        return
      }

      if (!shouldCarousel) {
        this.destroyCarousel()
        return
      }

      const { default: EmblaCarousel } = await import('embla-carousel')

      if (generation !== this.carouselGeneration) {
        return
      }

      this.embla = EmblaCarousel(this.$refs.viewport, {
        align: 'start',
        containScroll: 'trimSnaps',
      })

      this.embla.on('select', this.onSelect)
      this.onSelect()
    },

    /**
     *
     */
    onSelect() {
      this.activeIndex = this.embla.selectedScrollSnap()
    },

    /**
     *
     */
    destroyCarousel() {
      if (!this.embla) {
        return
      }

      this.embla.destroy()
      this.embla = undefined
      this.activeIndex = 0
    },

    /**
     * @param {number} index
     */
    scrollTo(index) {
      this.embla?.scrollTo(index)
    },

    /**
     * Navigate to the start of the redesigned funnel.
     */
    goToForm() {
      markFunnelEntered()
      this.$router.push('/v2/offertes-aanvragen/start')
    },
  },
}
</script>

<style lang="scss" scoped>
.how-it-works {
  background-color: #fafaf8;
  border-top: 1px solid #e8e8e4;
}

.how-it-works__eyebrow {
  @apply text-xs font-semibold uppercase md:text-base;
  color: #e8500a;
}

.how-it-works__title {
  @apply text-2xl font-bold md:text-[40px];
  color: #111111;
  line-height: 1.2;
  letter-spacing: -0.96px;

  @screen md {
    line-height: 1.1;
    letter-spacing: -2px;
  }
}

.how-it-works__subtitle {
  @apply text-xs md:text-lg;
  color: #555555;
  line-height: 1.6;
}

// Top padding is the badge's overhang; without it the overflow clip halves it.
.how-it-works__viewport {
  @apply overflow-hidden px-4;

  padding-top: 24px;

  @screen md {
    @apply overflow-visible px-0;

    padding-top: 0;
  }
}

.how-it-works__track {
  @apply flex items-stretch gap-4;

  @screen md {
    @apply flex-row;
  }
}

.how-it-works__card {
  // 90% hits the drawn 322px at 390 and keeps a peek on narrower phones.
  // Figma's mobile frames disagree on padding (49/40 vs 60/51), so one value.
  @apply relative flex flex-col items-center gap-6 bg-white px-6 py-10 text-center md:px-10 md:py-14;

  flex: 0 0 90%;
  max-width: 322px;
  border: 1px solid #e8e8e4;
  border-radius: 26.667px;

  @screen md {
    @apply mx-auto w-full;
    flex: 1 1 0;
    max-width: 444px;
  }
}

.how-it-works__badge {
  @apply absolute flex items-center justify-center rounded-full font-extrabold text-white;

  top: -22px;
  width: 44px;
  height: 44px;
  font-size: 15px;
  background-color: #ec6446;

  @screen md {
    top: -28px;
    width: 56px;
    height: 56px;
    font-size: 20px;
  }
}

.how-it-works__icon {
  @apply flex items-center justify-center;

  width: 44px;
  height: 44px;
  background-color: #fef2ec;
  border-radius: 8px;

  @screen md {
    width: 69.333px;
    height: 69.333px;
    border-radius: 16px;
  }
}

// Two lines' worth of box, so the body starts level across the three cards.
.how-it-works__card-title {
  @apply text-xl font-bold md:text-2xl;
  color: #111111;
  line-height: 1.1;
  letter-spacing: -1px;
  min-height: 2.2em;
}

.how-it-works__card-body {
  @apply text-xs md:text-base;
  color: #555555;
  line-height: 1.6;
}

// Pins the pill to the card's bottom so the three line up.
.how-it-works__pill {
  @apply mt-auto rounded-full text-sm font-semibold md:text-base;
  padding: 8px 16px;
  background-color: #fef2ec;
  color: #e8500a;
}

.how-it-works__arrow {
  color: #c8c8c4;
}

.how-it-works__dots {
  @apply mt-6 flex items-center justify-center gap-1;
}

// The visible pill is 7px tall; the button around it is a 44px tap target.
.how-it-works__dot {
  @apply relative flex shrink-0 items-center justify-center;

  width: 24px;
  height: 44px;

  &::after {
    content: '';
    display: block;
    width: 7px;
    height: 7px;
    border-radius: 56px;
    background-color: rgba(236, 100, 70, 0.25);
    transition:
      width 0.2s ease,
      background-color 0.2s ease;
  }
}

.how-it-works__dot--active {
  width: 40px;

  &::after {
    width: 23px;
    background-color: #e8500a;
  }
}

.how-it-works__cta {
  @apply w-full text-base font-semibold text-white transition duration-200 ease-in-out md:text-lg;

  height: 49px;
  background-color: #e8500a;
  border-radius: 100px;

  // Guarded so the hover state can't stick after a tap on touch devices.
  @media (hover: hover) {
    &:hover {
      @apply brightness-90;
    }
  }

  // Touch gets no hover, so the press is the feedback.
  &:active {
    @apply brightness-95;
  }

  @screen md {
    height: 67px;
    background-color: #ec6446;
    border: 1px solid #e8500a;
  }
}

.how-it-works__trust {
  @apply text-center text-xs md:text-base;
  color: #555555;
}
</style>

<i18n>
{
  "nl-NL": {
    "eyebrow": "HOE HET WERKT",
    "title": "In 3 stappen een geschikte makelaar gevonden",
    "subtitle": "Geen gedoe, geen verborgen kosten. Jij vergelijkt, wij regelen de rest.",
    "step1Title": "Vul je postcode in en beantwoord wat vragen",
    "step1Body": "Beantwoord een paar korte vragen over je situatie. Zo weten we precies welke makelaars bij je passen - je zit nergens aan vast.",
    "step1Pill": "Volledig veilig & vrijblijvend",
    "step2Title": "Ontvang gratis offertes",
    "step2Body": "Je ontvangt maximaal zes offertes van makelaars die aan je behoeften voldoen. Hiermee bespaar je tot 40%!",
    "step2Pill": "Bespaar tot 40%",
    "step3Title": "Vergelijk 6 makelaars",
    "step3Body": "Wij hebben onze makelaars al gecheckt, je hoeft alleen maar de offertes te vergelijken en te kiezen.",
    "step3Pill": "Klaar in 30 seconden",
    "dotLabel": "Ga naar stap {step}",
    "cta": "Start vergelijking",
    "trust": "Volledig gratis - Direct vergelijken - Snel antwoord"
  }
}
</i18n>
